import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../axios';
import { toggleSurveyPin, updateLinkedRoute } from '../../api/admin.api';
// S0-28 🔴 임시 조치 — 폐기 시 이 import 와 아래 `봇투표` 컴럼/상태를 함께 지운다.
import { BotVoteModal } from './BotVoteModal';

interface SurveyListItem {
  id: number;
  description: string;
  category: string;
  categoryEmoji: string;
  totalVoteCount: number;
  endDate: string;
  isExpired: boolean;
  isPinned: boolean;
  linkedRoute: string | null;
  linkedRouteLabel: string | null;
}

export const SurveyPinManagement: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleRows, setVisibleRows] = useState(10);
  const rowsPerPage = 10;
  const queryClient = useQueryClient();

  const { data: surveys = [], isLoading, error } = useQuery<SurveyListItem[]>({
    queryKey: ['adminSurveyList'],
    queryFn: async () => {
      const res = await apiClient.get('/api/question/getQuestions');
      return res.data.results ?? [];
    },
  });

  const pinMutation = useMutation({
    mutationFn: (id: number) => toggleSurveyPin(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminSurveyList'] });
    },
  });

  const linkedRouteMutation = useMutation({
    mutationFn: ({ id, linkedRoute, linkedRouteLabel }: { id: number; linkedRoute: string | null; linkedRouteLabel: string | null }) =>
      updateLinkedRoute(id, linkedRoute, linkedRouteLabel),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminSurveyList'] });
    },
  });

  // S0-28 봇 투표 모달 대상 설문 (null 이면 닫힌 상태)
  const [botVoteQuestionId, setBotVoteQuestionId] = useState<number | null>(null);

  const [editingLinkedRouteId, setEditingLinkedRouteId] = useState<number | null>(null);
  const [editingLinkedRouteValue, setEditingLinkedRouteValue] = useState('');
  const [editingLinkedRouteLabelValue, setEditingLinkedRouteLabelValue] = useState('');

  const totalPages = Math.ceil(surveys.length / rowsPerPage);
  const paginatedData = surveys.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const mobileData = surveys.slice(0, visibleRows);

  if (isLoading) {
    return <div className="p-6 text-center">로딩 중...</div>;
  }

  if (error) {
    return <div className="p-6 text-center text-red-500">설문 목록을 불러오지 못했습니다.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
        <h2 className="text-lg lg:text-xl font-bold text-[#030213]">설문 관리</h2>
        <p className="text-sm text-[#6B7280] mt-1">
          피드 상단에 고정할 설문을 선택하세요. 고정된 설문은 모든 사용자의 피드 최상단에 노출됩니다.
        </p>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#f3f3f5]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                설문 내용
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                카테고리
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                투표수
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                만료일
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                상태
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                연결링크
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                노출명
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                고정
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                봇투표
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-[#E5E7EB]">
            {paginatedData.map((survey) => (
              <tr
                key={survey.id}
                className={`hover:bg-[#f3f3f5] transition-colors ${survey.isPinned ? 'bg-amber-50' : ''}`}
              >
                <td className="px-6 py-4 text-sm text-[#030213] max-w-sm truncate">
                  {survey.isPinned && (
                    <span className="inline-block mr-1.5 text-amber-500">📌</span>
                  )}
                  {survey.description}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-[#6B7280]">
                  {survey.categoryEmoji} {survey.category}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-[#6B7280]">
                  {survey.totalVoteCount ?? 0}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-[#6B7280]">
                  {new Date(survey.endDate).toLocaleDateString('ko-KR')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                      survey.isExpired
                        ? 'bg-[#F3F4F6] text-[#6B7280]'
                        : 'bg-[#D1FAE5] text-[#065F46]'
                    }`}
                  >
                    {survey.isExpired ? '종료' : '진행중'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {editingLinkedRouteId === survey.id ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        value={editingLinkedRouteValue}
                        onChange={e => setEditingLinkedRouteValue(e.target.value)}
                        placeholder="/notice"
                        className="border rounded px-2 py-1 text-xs w-24"
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            linkedRouteMutation.mutate({
                              id: survey.id,
                              linkedRoute: editingLinkedRouteValue.trim() || null,
                              linkedRouteLabel: editingLinkedRouteLabelValue.trim() || null,
                            });
                            setEditingLinkedRouteId(null);
                          }
                          if (e.key === 'Escape') setEditingLinkedRouteId(null);
                        }}
                        autoFocus
                      />
                      <button
                        onClick={() => {
                          linkedRouteMutation.mutate({
                            id: survey.id,
                            linkedRoute: editingLinkedRouteValue.trim() || null,
                            linkedRouteLabel: editingLinkedRouteLabelValue.trim() || null,
                          });
                          setEditingLinkedRouteId(null);
                        }}
                        className="text-green-600 hover:text-green-700 text-xs"
                      >
                        ✓
                      </button>
                      <button
                        onClick={() => setEditingLinkedRouteId(null)}
                        className="text-gray-400 hover:text-gray-600 text-xs"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setEditingLinkedRouteId(survey.id);
                        setEditingLinkedRouteValue(survey.linkedRoute ?? '');
                        setEditingLinkedRouteLabelValue(survey.linkedRouteLabel ?? '');
                      }}
                      className={`px-2 py-1 text-xs rounded ${
                        survey.linkedRoute
                          ? 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                          : 'bg-[#f3f3f5] text-[#6B7280] hover:bg-[#E5E7EB]'
                      }`}
                    >
                      {survey.linkedRoute ?? '설정'}
                    </button>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {editingLinkedRouteId === survey.id ? (
                    <input
                      type="text"
                      value={editingLinkedRouteLabelValue}
                      onChange={e => setEditingLinkedRouteLabelValue(e.target.value)}
                      placeholder="공지사항 보기"
                      className="border rounded px-2 py-1 text-xs w-28"
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          linkedRouteMutation.mutate({
                            id: survey.id,
                            linkedRoute: editingLinkedRouteValue.trim() || null,
                            linkedRouteLabel: editingLinkedRouteLabelValue.trim() || null,
                          });
                          setEditingLinkedRouteId(null);
                        }
                        if (e.key === 'Escape') setEditingLinkedRouteId(null);
                      }}
                    />
                  ) : (
                    <span className={`text-xs ${survey.linkedRouteLabel ? 'text-[#030213]' : 'text-[#9CA3AF]'}`}>
                      {survey.linkedRouteLabel ?? (survey.linkedRoute ? '바로가기' : '-')}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => pinMutation.mutate(survey.id)}
                    disabled={pinMutation.isPending}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 ${
                      survey.isPinned
                        ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                        : 'bg-[#f3f3f5] text-[#6B7280] hover:bg-[#E5E7EB]'
                    }`}
                  >
                    {survey.isPinned ? '📌 고정 해제' : '고정'}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => setBotVoteQuestionId(survey.id)}
                    disabled={survey.isExpired}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#f3f3f5] text-[#6B7280] hover:bg-[#E5E7EB] disabled:opacity-40 disabled:hover:bg-[#f3f3f5]"
                  >
                    봇투표
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 p-4 border-t border-[#E5E7EB]">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border border-[#E5E7EB] rounded hover:bg-[#f3f3f5] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              이전
            </button>
            <span className="text-sm text-[#6B7280]">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border border-[#E5E7EB] rounded hover:bg-[#f3f3f5] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              다음
            </button>
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {mobileData.map((survey) => (
          <div
            key={survey.id}
            className={`bg-white rounded-lg shadow-sm p-4 ${survey.isPinned ? 'border border-amber-300' : ''}`}
          >
            <div className="flex justify-between items-start mb-2">
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${
                  survey.isExpired
                    ? 'bg-[#F3F4F6] text-[#6B7280]'
                    : 'bg-[#D1FAE5] text-[#065F46]'
                }`}
              >
                {survey.isExpired ? '종료' : '진행중'}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => pinMutation.mutate(survey.id)}
                  disabled={pinMutation.isPending}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 ${
                    survey.isPinned
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-[#f3f3f5] text-[#6B7280]'
                  }`}
                >
                  {survey.isPinned ? '📌 고정 해제' : '고정'}
                </button>
                {/* S0-28 🔴 임시 조치 — 폐기 시 이 버튼도 데스크톱 컬럼과 함께 지운다. */}
                <button
                  onClick={() => setBotVoteQuestionId(survey.id)}
                  disabled={survey.isExpired}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#f3f3f5] text-[#6B7280] disabled:opacity-40"
                >
                  봇투표
                </button>
              </div>
            </div>
            <p className="text-sm font-medium text-[#030213] mb-2 line-clamp-2">
              {survey.isPinned && <span className="mr-1 text-amber-500">📌</span>}
              {survey.description}
            </p>
            <div className="flex justify-between items-center text-xs text-[#6B7280]">
              <span>{survey.categoryEmoji} {survey.category}</span>
              <span>투표 {survey.totalVoteCount ?? 0}건</span>
            </div>
          </div>
        ))}

        {visibleRows < surveys.length && (
          <button
            onClick={() => setVisibleRows((prev) => Math.min(prev + 10, surveys.length))}
            className="w-full py-3 text-sm font-medium text-[#030213] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#f3f3f5] transition-colors"
          >
            더 보기
          </button>
        )}
      </div>

      {/* S0-28 봇 투표 모달 — 🔴 임시 조치, 폐기 시 함께 제거 */}
      {botVoteQuestionId !== null && (
        <BotVoteModal
          questionId={botVoteQuestionId}
          onClose={() => setBotVoteQuestionId(null)}
        />
      )}
    </div>
  );
};
